import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  FileText, Download, Users, Home, Briefcase, Target, TrendingUp,
  RefreshCw, DollarSign, CheckCircle, BarChart3, Clock, Heart
} from 'lucide-react';
import { toast } from 'sonner';

export default function ImpactReportPage() {
  const { axiosAuth } = useAuth();
  const [period, setPeriod] = useState('quarter');
  const [impactData, setImpactData] = useState(null);
  const [grantData, setGrantData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const api = axiosAuth();

  const fetchData = async () => {
    setLoading(true);
    try {
      const [impactRes, grantRes] = await Promise.all([
        api.get(`/impact/summary?period=${period}`),
        api.get(`/reports/grant-ready?period=${period}`)
      ]);
      setImpactData(impactRes.data);
      setGrantData(grantRes.data);
    } catch (err) {
      toast.error('Failed to load impact report');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, [period]);

  const downloadPDF = async () => {
    setDownloading(true);
    try {
      const response = await api.get(`/reports/impact/pdf?period=${period}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `impact_report_${period}_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Impact report downloaded!');
    } catch (err) {
      toast.error('Failed to download report');
    }
    setDownloading(false);
  };

  const downloadCSV = async () => {
    try {
      const response = await api.get(`/impact/export?period=${period}&format=csv`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `impact_data_${period}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('CSV exported!');
    } catch (err) {
      toast.error('Failed to export CSV');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);
  };

  if (loading && !impactData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-3 border-indigo-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const metrics = impactData?.metrics || {};

  return (
    <div className="space-y-6" data-testid="impact-report-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-foreground" style={{ fontFamily: 'Manrope' }}>
            Impact Report Generator
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Generate professional reports for funders and stakeholders
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-36 rounded-xl">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="week">Weekly</SelectItem>
              <SelectItem value="month">Monthly</SelectItem>
              <SelectItem value="quarter">Quarterly</SelectItem>
              <SelectItem value="year">Annual</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={fetchData} variant="outline" className="gap-2 rounded-full" disabled={loading}>
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} /> Refresh
          </Button>
          <Button onClick={downloadCSV} variant="outline" className="gap-2 rounded-full">
            <Download size={16} /> CSV
          </Button>
          <Button onClick={downloadPDF} className="bg-indigo-600 hover:bg-indigo-700 text-white gap-2 rounded-full" disabled={downloading}>
            <FileText size={16} /> {downloading ? 'Generating...' : 'Download PDF'}
          </Button>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-0 shadow-sm rounded-2xl">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center">
                <Users className="text-indigo-600" size={20} />
              </div>
            </div>
            <p className="text-2xl font-bold">{metrics.participants_served || 0}</p>
            <p className="text-xs text-muted-foreground">Participants Served</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm rounded-2xl">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center">
                <Home className="text-emerald-600" size={20} />
              </div>
            </div>
            <p className="text-2xl font-bold">{metrics.housing_placements || 0}</p>
            <p className="text-xs text-muted-foreground">Housing Placements</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm rounded-2xl">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center">
                <Clock className="text-amber-600" size={20} />
              </div>
            </div>
            <p className="text-2xl font-bold">{metrics.service_hours_total || 0}</p>
            <p className="text-xs text-muted-foreground">Service Hours</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm rounded-2xl">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-10 h-10 rounded-xl bg-violet-50 flex items-center justify-center">
                <Target className="text-violet-600" size={20} />
              </div>
            </div>
            <p className="text-2xl font-bold">{metrics.goals_completed || 0}</p>
            <p className="text-xs text-muted-foreground">Goals Achieved</p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList className="bg-muted/50 rounded-full p-1">
          <TabsTrigger value="overview" className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm px-4">
            Overview
          </TabsTrigger>
          <TabsTrigger value="services" className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm px-4">
            Services
          </TabsTrigger>
          <TabsTrigger value="outcomes" className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm px-4">
            Outcomes
          </TabsTrigger>
          <TabsTrigger value="grant" className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm px-4">
            Grant Data
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          <Card className="border-0 shadow-sm rounded-2xl bg-gradient-to-r from-indigo-50 to-violet-50">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold" style={{ fontFamily: 'Manrope' }}>
                Report Period: {impactData?.period_start} to {impactData?.period_end}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">
                During this {period} reporting period, we served <strong>{metrics.participants_served || 0}</strong> participants,
                including <strong>{metrics.new_participants || 0}</strong> new enrollments. Our team delivered{' '}
                <strong>{metrics.service_hours_total || 0}</strong> hours of direct services through{' '}
                <strong>{metrics.peer_sessions_delivered || 0}</strong> peer support sessions and{' '}
                <strong>{metrics.group_sessions_held || 0}</strong> group sessions.
              </p>
            </CardContent>
          </Card>

          <div className="grid md:grid-cols-2 gap-4">
            <Card className="border-0 shadow-sm rounded-2xl">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                  <TrendingUp className="text-emerald-600" size={18} /> Growth Metrics
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between items-center p-3 bg-muted/30 rounded-xl">
                  <span>New Participants</span>
                  <Badge className="bg-emerald-100 text-emerald-700">{metrics.new_participants || 0}</Badge>
                </div>
                <div className="flex justify-between items-center p-3 bg-muted/30 rounded-xl">
                  <span>Referrals Received</span>
                  <Badge className="bg-sky-100 text-sky-700">{metrics.referrals_received || 0}</Badge>
                </div>
                <div className="flex justify-between items-center p-3 bg-muted/30 rounded-xl">
                  <span>Housing Placements</span>
                  <Badge className="bg-violet-100 text-violet-700">{metrics.housing_placements || 0}</Badge>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-sm rounded-2xl">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                  <CheckCircle className="text-indigo-600" size={18} /> Outcome Improvements
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between items-center p-3 bg-muted/30 rounded-xl">
                  <span>Housing Stability</span>
                  <Badge className="bg-emerald-100 text-emerald-700">+{metrics.housing_improvements || 0}</Badge>
                </div>
                <div className="flex justify-between items-center p-3 bg-muted/30 rounded-xl">
                  <span>Employment</span>
                  <Badge className="bg-sky-100 text-sky-700">+{metrics.employment_improvements || 0}</Badge>
                </div>
                <div className="flex justify-between items-center p-3 bg-muted/30 rounded-xl">
                  <span>Recovery Engagement</span>
                  <Badge className="bg-violet-100 text-violet-700">+{metrics.recovery_improvements || 0}</Badge>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Services Tab */}
        <TabsContent value="services" className="space-y-4">
          <div className="grid md:grid-cols-3 gap-4">
            <Card className="border-0 shadow-sm rounded-2xl">
              <CardContent className="p-6 text-center">
                <Heart className="w-10 h-10 text-rose-500 mx-auto mb-3" />
                <p className="text-3xl font-bold">{metrics.peer_sessions_delivered || 0}</p>
                <p className="text-sm text-muted-foreground">Peer Support Sessions</p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-sm rounded-2xl">
              <CardContent className="p-6 text-center">
                <Users className="w-10 h-10 text-indigo-500 mx-auto mb-3" />
                <p className="text-3xl font-bold">{metrics.group_sessions_held || 0}</p>
                <p className="text-sm text-muted-foreground">Group Sessions Held</p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-sm rounded-2xl">
              <CardContent className="p-6 text-center">
                <BarChart3 className="w-10 h-10 text-emerald-500 mx-auto mb-3" />
                <p className="text-3xl font-bold">{metrics.group_attendance_total || 0}</p>
                <p className="text-sm text-muted-foreground">Total Group Attendance</p>
              </CardContent>
            </Card>
          </div>

          <Card className="border-0 shadow-sm rounded-2xl">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold" style={{ fontFamily: 'Manrope' }}>
                Service Delivery Summary
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 px-3 font-medium">Service Type</th>
                      <th className="text-right py-2 px-3 font-medium">Count</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b">
                      <td className="py-2.5 px-3">Peer Support Contacts</td>
                      <td className="py-2.5 px-3 text-right font-medium">{metrics.peer_sessions_delivered || 0}</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2.5 px-3">Group Sessions</td>
                      <td className="py-2.5 px-3 text-right font-medium">{metrics.group_sessions_held || 0}</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2.5 px-3">Employment Services</td>
                      <td className="py-2.5 px-3 text-right font-medium">{metrics.employment_services || 0}</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2.5 px-3">Daily Check-ins</td>
                      <td className="py-2.5 px-3 text-right font-medium">{metrics.checkins_completed || 0}</td>
                    </tr>
                    <tr className="border-b bg-muted/30">
                      <td className="py-2.5 px-3 font-medium">Total Service Hours</td>
                      <td className="py-2.5 px-3 text-right font-bold">{metrics.service_hours_total || 0}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Outcomes Tab */}
        <TabsContent value="outcomes" className="space-y-4">
          <div className="grid md:grid-cols-3 gap-4">
            <Card className="border-0 shadow-sm rounded-2xl border-l-4 border-l-emerald-500">
              <CardContent className="p-6">
                <Home className="w-8 h-8 text-emerald-500 mb-3" />
                <p className="text-3xl font-bold">{metrics.housing_improvements || 0}</p>
                <p className="text-sm text-muted-foreground">Housing Stability Improvements</p>
                <p className="text-xs text-emerald-600 mt-2">Participants moved to more stable housing situations</p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-sm rounded-2xl border-l-4 border-l-sky-500">
              <CardContent className="p-6">
                <Briefcase className="w-8 h-8 text-sky-500 mb-3" />
                <p className="text-3xl font-bold">{metrics.employment_improvements || 0}</p>
                <p className="text-sm text-muted-foreground">Employment Improvements</p>
                <p className="text-xs text-sky-600 mt-2">Participants gained employment or better positions</p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-sm rounded-2xl border-l-4 border-l-violet-500">
              <CardContent className="p-6">
                <Heart className="w-8 h-8 text-violet-500 mb-3" />
                <p className="text-3xl font-bold">{metrics.recovery_improvements || 0}</p>
                <p className="text-sm text-muted-foreground">Recovery Engagement Improvements</p>
                <p className="text-xs text-violet-600 mt-2">Increased participation in recovery activities</p>
              </CardContent>
            </Card>
          </div>

          <Card className="border-0 shadow-sm rounded-2xl">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold" style={{ fontFamily: 'Manrope' }}>
                Goals & Milestones
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-center gap-8 py-6">
                <div className="text-center">
                  <p className="text-4xl font-bold text-indigo-600">{metrics.goals_completed || 0}</p>
                  <p className="text-sm text-muted-foreground">Goals Completed</p>
                </div>
                <div className="h-16 w-px bg-muted" />
                <div className="text-center">
                  <p className="text-4xl font-bold text-emerald-600">{metrics.housing_placements || 0}</p>
                  <p className="text-sm text-muted-foreground">Housing Placements</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Grant Data Tab */}
        <TabsContent value="grant" className="space-y-4">
          {grantData && (
            <>
              <Card className="border-0 shadow-sm rounded-2xl bg-gradient-to-r from-amber-50 to-orange-50 border-l-4 border-l-amber-500">
                <CardContent className="p-4">
                  <p className="font-medium text-amber-800 mb-1">Grant-Ready Data Export</p>
                  <p className="text-sm text-amber-700">
                    All metrics below are formatted for common grant reporting requirements. 
                    Download the PDF for a professional funder presentation.
                  </p>
                </CardContent>
              </Card>

              <div className="grid md:grid-cols-2 gap-4">
                <Card className="border-0 shadow-sm rounded-2xl">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base font-semibold" style={{ fontFamily: 'Manrope' }}>
                      Service Delivery
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {Object.entries(grantData.service_delivery || {}).map(([key, value]) => (
                      <div key={key} className="flex justify-between p-2 bg-muted/20 rounded-lg text-sm">
                        <span className="capitalize">{key.replace(/_/g, ' ')}</span>
                        <span className="font-medium">{value}</span>
                      </div>
                    ))}
                  </CardContent>
                </Card>

                <Card className="border-0 shadow-sm rounded-2xl">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base font-semibold" style={{ fontFamily: 'Manrope' }}>
                      Cost Effectiveness
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {Object.entries(grantData.cost_effectiveness || {}).map(([key, value]) => (
                      <div key={key} className="flex justify-between p-2 bg-muted/20 rounded-lg text-sm">
                        <span className="capitalize">{key.replace(/_/g, ' ')}</span>
                        <span className="font-medium">
                          {typeof value === 'number' && key.includes('savings') ? formatCurrency(value) : value}
                          {key.includes('percentage') ? '%' : ''}
                        </span>
                      </div>
                    ))}
                  </CardContent>
                </Card>

                <Card className="border-0 shadow-sm rounded-2xl">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base font-semibold" style={{ fontFamily: 'Manrope' }}>
                      Outcomes
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {Object.entries(grantData.outcomes || {}).map(([key, value]) => (
                      <div key={key} className="flex justify-between p-2 bg-muted/20 rounded-lg text-sm">
                        <span className="capitalize">{key.replace(/_/g, ' ')}</span>
                        <span className="font-medium">{value}</span>
                      </div>
                    ))}
                  </CardContent>
                </Card>

                <Card className="border-0 shadow-sm rounded-2xl">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base font-semibold" style={{ fontFamily: 'Manrope' }}>
                      Recidivism Data
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {Object.entries(grantData.recidivism_data || {}).map(([key, value]) => (
                      <div key={key} className="flex justify-between p-2 bg-muted/20 rounded-lg text-sm">
                        <span className="capitalize">{key.replace(/_/g, ' ')}</span>
                        <span className="font-medium">
                          {key.includes('rate') || key.includes('comparison') ? `${value}%` : value}
                        </span>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
